import { computed, onBeforeUnmount, onMounted, ref, Ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Swal from 'sweetalert2/dist/sweetalert2.min.js';
import moment from 'moment';
import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { hasFeature } from '@/core/helpers/feature';
import posthog from '@/posthog';
import type { LabKind } from './labs';

interface LabInstanceOptions {
  // Chamado depois que uma flag é aceita (o recurso já foi recarregado no store)
  onFlagAccepted?: () => void
  pathwayId?: string
}

// Estado e ações de uma instância de laboratório (desafio ou máquina)
export const useLabInstance = (kind: LabKind, resource: Ref<any>, options: LabInstanceOptions = {}) => {
  const store = useStore();
  const { t, te } = useI18n();
  const lang = computed(() => store.getters.getCurrentLanguage || localStorage.getItem('currentLanguage') || 'en');

  const tg = (key: string) => t(`${lang.value}.${key}`);
  // Textos de laboratório existem em "challenge.*" e (quase todos) em "machine.*"
  const tk = (key: string) => te(`${lang.value}.${kind}.${key}`) ? tg(`${kind}.${key}`) : tg(`challenge.${key}`);

  const loading = ref(false);
  const submitting = ref(false);
  const flag = ref('');
  const countdown = ref('');
  const releaseCountdown = ref('');
  const copied = ref(false);

  const endpoint = computed(() => `labs/${kind === 'challenge' ? 'challenges' : 'machines'}/${resource.value.id}`);
  const canStart = computed(() =>
    hasFeature(kind === 'challenge' ? 'challenge_access' : 'machines_access') || Boolean(resource.value.is_freemium)
  );
  const instance = computed(() => resource.value.instance);
  const isActive = computed(() => Boolean(instance.value?.is_active));

  const pad = (value: number) => String(Math.max(value, 0)).padStart(2, '0');

  const playAlert = () => {
    new Audio('/media/alert.mp3').play().catch(() => undefined);
  };

  const tick = () => {
    if (isActive.value && instance.value?.shutdown) {
      const remaining = Math.floor(moment.utc(instance.value.shutdown).diff(moment.utc()) / 1000);
      // Avisos sonoros com 1:59, 0:59 e 0:20 restantes
      if (remaining === 119 || remaining === 59 || remaining === 20) playAlert();
      countdown.value = remaining > 0
        ? `${pad(Math.floor(remaining / 3600))}:${pad(Math.floor((remaining % 3600) / 60))}:${pad(remaining % 60)}`
        : '';
    } else {
      countdown.value = '';
    }

    if (resource.value.release_at) {
      const duration = moment.duration(moment.utc(resource.value.release_at).diff(moment.utc()));
      releaseCountdown.value = duration.asSeconds() > 0
        ? `${Math.floor(duration.asDays())}d ${duration.hours()}h ${duration.minutes()}m ${duration.seconds()}s`
        : '';
    } else {
      releaseCountdown.value = '';
    }
  };

  let timer: number | undefined;
  onMounted(() => {
    tick();
    timer = window.setInterval(tick, 1000);
  });
  onBeforeUnmount(() => window.clearInterval(timer));

  const payloadOf = (data: any) => kind === 'challenge' ? data.challenge : data.machine;

  const commitResource = (value: any) => {
    if (!value) return;
    // Alguns endpoints de instância (ex.: addTime) não carregam os anexos; mantém os que já temos
    if (!value.attachments && String(value.id) === String(resource.value?.id) && resource.value?.attachments) {
      value = { ...value, attachments: resource.value.attachments };
    }
    store.commit(kind === 'challenge' ? Mutations.SET_CHALLENGES : Mutations.SET_CURRENT_MACHINE, value);
  };

  // Recarrega o usuário para o indicador de instância ligada no topo refletir na hora
  const refreshUser = () => store.dispatch(Actions.VERIFY_AUTH);

  const refetchResource = () => kind === 'challenge'
    ? store.dispatch(Actions.GET_CHALLENGE, resource.value.id)
    : store.dispatch(Actions.GET_MACHINE, { id: resource.value.id });

  const alertError = (text: string, icon = 'error') => Swal.fire({ title: 'Oops...', text, icon, confirmButtonText: 'Ok' });

  const start = async () => {
    if (loading.value || !canStart.value) return;
    loading.value = true;
    try {
      const { data } = await ApiService.get(`${endpoint.value}/start`);
      if (data.success) {
        const started = payloadOf(data);
        commitResource(started);
        refreshUser();
        posthog.capture(kind === 'challenge' ? 'started_challenge_instance' : 'started_machine_instance', {
          id: resource.value.id,
          name: started?.name,
          difficulty: started?.difficulty ?? started?.dificulty,
          points: started?.points,
          is_freemium: started?.is_freemium,
        });
        tick();
      } else {
        alertError(data.message);
      }
    } catch (error: any) {
      const message = error?.response?.data?.message;
      alertError(message || tg('messages.we_had_a_problem_starting_your_instance_please_try_again_later'), message ? 'warning' : 'error');
    } finally {
      loading.value = false;
    }
  };

  const stop = async () => {
    if (loading.value) return;
    loading.value = true;
    try {
      const { data } = await ApiService.get(`${endpoint.value}/stop`);
      if (!data.success) alertError(data.message);
      commitResource(payloadOf(data));
      refreshUser();
    } catch (error) {
      alertError(tg('messages.we_had_a_problem_closing_your_instance_contact_support'));
    } finally {
      loading.value = false;
    }
  };

  const addTime = async () => {
    if (loading.value) return;
    loading.value = true;
    try {
      const { data } = await ApiService.get(`${endpoint.value}/addTime`);
      if (data.success) {
        commitResource(payloadOf(data));
        tick();
        Swal.fire({
          title: tg('messages.congratulations'),
          text: data.message || tg('messages.you_ve_added_more_time_to_your_instance'),
          icon: 'success',
          confirmButtonText: 'Ok',
        });
      } else {
        alertError(data.message);
      }
    } catch (error) {
      alertError(tg('messages.we_had_a_problem_adding_time_to_your_instance_please_try_again_later'));
    } finally {
      loading.value = false;
    }
  };

  const submitFlag = async () => {
    if (!flag.value.trim()) {
      alertError(tg('messages.please_insert_a_flag'));
      return;
    }
    if (submitting.value) return;
    submitting.value = true;
    try {
      const path = kind === 'challenge' ? 'challenges' : 'machines';
      const { data } = await ApiService.vueInstance.axios.put(`/labs/${path}/pown/${resource.value.id}`, { flag: flag.value.trim() });
      if (data.success) {
        const alreadySent = data.message && (data.message.toLowerCase().includes('já enviou') || data.message.toLowerCase().includes('already sent'));
        Swal.fire({
          title: tg('messages.congratulations'),
          text: alreadySent ? data.message : tg('messages.you_hit_the_flag'),
          icon: alreadySent ? 'info' : 'success',
          confirmButtonText: 'Ok',
        });
        flag.value = '';
        if (options.pathwayId) store.dispatch(Actions.GET_PATHWAY, options.pathwayId);
        await refetchResource();
        options.onFlagAccepted?.();
      } else {
        alertError(data.message);
      }
    } catch (error) {
      alertError(tg('messages.we_had_a_problem_verifying_your_flag_please_try_again'));
    } finally {
      submitting.value = false;
    }
  };

  const copyIp = async () => {
    try {
      await navigator.clipboard.writeText(instance.value?.ip_address || '');
      copied.value = true;
      window.setTimeout(() => { copied.value = false; }, 1500);
    } catch (error) {
      console.error('Clipboard error:', error);
    }
  };

  return {
    tg,
    tk,
    loading,
    submitting,
    flag,
    countdown,
    releaseCountdown,
    copied,
    canStart,
    instance,
    isActive,
    start,
    stop,
    addTime,
    submitFlag,
    copyIp,
  };
};
